<template>
  <v-row>
    <v-col cols="12">
      <v-row class="section-container">
        <v-col cols="12">
          <p class="text-body-3-1 text-color-blue-gray weight-400 mt-2">
            Attestation: I confirm that the information provided within this Prior Authorization form is accurate and
            true. I understand that any person who knowingly makes or causes a false record or statement that is
            signicant to a claim paid by the United States government or any state government may be subject to civil
            penalties under both the federal and state laws. I further confirm that I have reviewed the signature
            requirements within this Prior Authorization form. By signing this form, I represent that I am authorized to
            submit PAs on behalf of the NPI associated with this Prior Authorization
          </p>
          <v-checkbox
            v-model="acceptTermsOfService"
            :rules="[(v) => !!v || 'You must agree to continue']"
            :disabled="props.disabled"
            label="I confirm that I have read and consent with the Attestation above."
            :error="props.formButtonClicked && !acceptTermsOfService"
            :error-messages="
              props.formButtonClicked && !acceptTermsOfService
                ? 'Must confirm that you are authorized to submit Prior Authorizations on behalf of the NPI associated with this Prior Authorization'
                : ''
            "></v-checkbox>

          <v-sheet class="mb-4 rounded-16" color="background">
            <div class="signature-input">
              <FeedbackInputWrapper
                :field-id="'signature'"
                :order-key="props.orderKey"
                :model-value="signature"
                :error-messages="props.formButtonClicked && !signature ? 'Please provide your signature' : null"
                label="signature"
                @update:model-value="handleSignatureUpdate">
                <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                  <v-text-field
                    :model-value="signature"
                    type="text"
                    label="Signature"
                    placeholder="Signature"
                    class="signature-field"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :disabled="props.disabled"
                    :error-messages="errorMessages"
                    @update:model-value="handleSignatureUpdate"
                    @input="onInput"
                    @change="onChange"
                    @focus="onFocus"
                    @blur="onBlur" />
                </template>
              </FeedbackInputWrapper>
            </div>
          </v-sheet>

          <p class="text-body-3-1 text-color-blue-gray weight-400 mt-2">
            Disclaimer: By providing my signature above, I acknowledge that my signature (1) will be electronically
            stored; and (2) may be reproduced by this Do Prior Auth user account and additionally, those user accounts
            linked to this NPI. Additionally, I expressly authorize such users to reproduce by signature when utilizing
            the Do Prior Auth platform for the purposes of completion of prior authorizations, enrollment forms,
            e-appeals or other available functionality.
          </p>

          <template v-if="props.electronicSignatureEnabled">
            <p class="text-body-3-1 text-color-blue-gray weight-400 mt-4">
              The name above and the signature you draw below will be applied to this submission's fax form when it's
              sent.
            </p>
            <v-sheet class="mb-4 rounded-16" color="background">
              <canvas ref="canvasRef" class="signature-pad rounded-16"></canvas>
            </v-sheet>
            <v-row justify="end" class="mt-n2">
              <v-col cols="auto">
                <v-btn variant="outlined" color="primary" :disabled="props.disabled" @click="clearSignatureImage">
                  Clear
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, watch, toRef } from "vue";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";
import { useSignaturePad } from "@/composables/useSignaturePad";

const props = defineProps({
  orderKey: {
    type: String,
    default: "",
  },
  signature: {
    type: String,
    default: "",
  },
  formButtonClicked: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  electronicSignatureEnabled: {
    type: Boolean,
    default: false,
  },
  signatureImage: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["updateSignature", "update:signatureImage"]);

const signature = ref(props.signature);
const acceptTermsOfService = ref(props.signature !== null && props.signature !== "");

// Watch for prop changes and update local ref
watch(
  () => props.signature,
  (newValue) => {
    signature.value = newValue;
  }
);

// Watch for local changes and emit updates
watch(
  () => signature.value,
  (newValue) => {
    emit("updateSignature", newValue);
  }
);

const handleSignatureUpdate = (value) => {
  signature.value = value;
  emit("updateSignature", value);
};

// See useSignaturePad's own docstring for why it watches the canvas ref rather than a single
// gating condition — this component isn't v-if-gated, and the "Signature" panel is a
// lazy-rendering v-expansion-panel besides, so the canvas can appear well after mount.
const { canvasRef, clear: clearSignatureImage } = useSignaturePad({
  modelValue: toRef(props, "signatureImage"),
  disabled: toRef(props, "disabled"),
  onChange: (value) => emit("update:signatureImage", value),
});
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";

.signature-input {
  .signature-field {
    :deep(.v-field__input) {
      font-style: italic;
      font-family: "Brush Script MT", cursive;
      font-size: 1.2em;
    }
  }
}

.signature-pad {
  width: 100%;
  height: 200px;
  background-color: white;
}
</style>
